import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { useAuth } from '../../lib/auth';
import { useNotifications } from '../../lib/notifications';
import { DURATION, EASE_OUT_EXPO } from '../../lib/motion';
import { Button, ButtonLink, cx } from '../ui';
import { ThemeToggle } from './ThemeToggle';

const LANDING_LINKS = [
  { href: '#how', label: 'Как это работает' },
  { href: '#showcase', label: 'Готовые маршруты' },
  { href: '#ai', label: 'AI-планировщик' },
  { href: '#privacy', label: 'Приватность' },
  { href: '/deals', label: 'Выгодные предложения' },
];

/**
 * Header.
 *
 * Transparent over the hero, then it condenses into a frosted bar once the page
 * scrolls — the single most effective "this is a modern product" cue, and it
 * costs one scroll listener.
 */
export function SiteHeader() {
  const { user, status, logout } = useAuth();
  const { scrollY } = useScroll();
  const [condensed, setCondensed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === '/';

  useMotionValueEvent(scrollY, 'change', (value) => {
    setCondensed(value > 24);
  });

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      <motion.header
        data-site-header
        initial={false}
        animate={{
          backgroundColor: condensed || !isLanding ? 'var(--header-bg)' : 'rgba(0,0,0,0)',
          borderBottomColor: condensed || !isLanding ? 'var(--border-subtle)' : 'rgba(0,0,0,0)',
        }}
        transition={{ duration: DURATION.fast, ease: EASE_OUT_EXPO }}
        style={
          {
            '--header-bg': 'color-mix(in srgb, var(--surface) 82%, transparent)',
          } as React.CSSProperties
        }
        className="fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl backdrop-saturate-150"
      >
        <div className="container-page flex h-16 items-center justify-between gap-3 md:h-[4.5rem] md:gap-6">
          <Link
            to="/"
            className="group flex min-w-0 items-center gap-2.5"
            aria-label="Travessa — на главную"
          >
            <motion.span
              className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-clay-500 shadow-[0_6px_18px_-6px_rgba(226,101,74,0.8)]"
              whileHover={{ rotate: 18, scale: 1.06 }}
              transition={{ duration: DURATION.fast, ease: EASE_OUT_EXPO }}
            >
              <img src="/assets/travessa-mark.png" alt="" className="size-full object-cover" />
            </motion.span>
            <span className="display truncate text-2xl tracking-tight">Travessa</span>
          </Link>

          {isLanding ? (
            <nav className="hidden items-center gap-7 lg:flex">
              {LANDING_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="link-underline text-sm text-[var(--text-muted)] transition-colors hover:text-[var(--text-strong)]"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          ) : (
            <nav className="hidden items-center gap-1 md:flex">
              <HeaderLink to="/app">Мои путешествия</HeaderLink>
              <HeaderLink to="/gallery">Витрина</HeaderLink>
              <HeaderLink to="/deals">Выгодно</HeaderLink>
              {user?.role === 'admin' ? <HeaderLink to="/admin">Админка</HeaderLink> : null}
            </nav>
          )}

          <div className="flex shrink-0 items-center gap-2">
            <ThemeToggle />
            {status === 'authenticated' && user ? <NotificationBell /> : null}

            {/* Inline actions wrap in a `hidden lg:flex` container: putting
                `hidden` directly on a Button/ButtonLink fails, because their
                base `inline-flex` wins over `hidden` by CSS source order. The
                wrapper has no such conflict, so the whole group hides below lg
                and lives in the hamburger menu instead. */}
            <div className="hidden items-center gap-2 lg:flex">
              {status === 'authenticated' && user ? (
                <>
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 rounded-full border border-[var(--border-subtle)] py-1.5 pl-1.5 pr-4 text-sm transition-colors hover:bg-[var(--surface-sunken)]"
                  >
                    <span className="grid size-7 place-items-center rounded-full bg-lagoon-500/15 text-sm">
                      {user.avatarEmoji ?? '🙂'}
                    </span>
                    {user.displayName ?? user.handle}
                  </Link>
                  <ButtonLink to="/app" size="sm">
                    В приложение
                  </ButtonLink>
                  <Button size="sm" variant="ghost" onClick={() => void logout()}>
                    Выйти
                  </Button>
                </>
              ) : status === 'anonymous' ? (
                <>
                  <ButtonLink to="/login" variant="ghost" size="sm">
                    Войти
                  </ButtonLink>
                  <ButtonLink to="/register" size="sm">
                    Начать
                  </ButtonLink>
                </>
              ) : null}
            </div>

            <button
              type="button"
              aria-label="Меню"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="grid size-10 shrink-0 place-items-center rounded-full border border-[var(--border-subtle)] lg:hidden"
            >
              <span className="relative block h-3 w-4">
                <motion.span
                  animate={menuOpen ? { rotate: 45, y: 5 } : { rotate: 0, y: 0 }}
                  className="absolute inset-x-0 top-0 h-0.5 rounded bg-current"
                />
                <motion.span
                  animate={menuOpen ? { opacity: 0 } : { opacity: 1 }}
                  className="absolute inset-x-0 top-[5px] h-0.5 rounded bg-current"
                />
                <motion.span
                  animate={menuOpen ? { rotate: -45, y: -5 } : { rotate: 0, y: 0 }}
                  className="absolute inset-x-0 top-2.5 h-0.5 rounded bg-current"
                />
              </span>
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DURATION.fast }}
            className="fixed inset-0 z-40 overflow-y-auto overscroll-contain bg-[var(--surface)] pt-20 pb-10 lg:hidden"
          >
            <motion.nav
              initial="hidden"
              animate="visible"
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } } }}
              className="container-page flex flex-col gap-1"
            >
              {(isLanding
                ? LANDING_LINKS.map((l) => ({ to: l.href, label: l.label, external: true }))
                : user
                  ? [
                      { to: '/app', label: 'Мои путешествия', external: false },
                      { to: '/gallery', label: 'Витрина', external: false },
                      { to: '/deals', label: 'Выгодные предложения', external: false },
                      { to: '/settings', label: 'Настройки', external: false },
                      ...(user.role === 'admin'
                        ? [{ to: '/admin', label: 'Админка', external: false }]
                        : []),
                    ]
                  : [
                      { to: '/gallery', label: 'Витрина', external: false },
                      { to: '/deals', label: 'Выгодные предложения', external: false },
                    ]
              ).map((link) =>
                link.external ? (
                  <motion.a
                    key={link.to}
                    href={link.to}
                    onClick={() => setMenuOpen(false)}
                    variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0 } }}
                    className="display border-b border-[var(--border-subtle)] py-4 text-[2rem] leading-tight"
                  >
                    {link.label}
                  </motion.a>
                ) : (
                  <motion.div
                    key={link.to}
                    variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0 } }}
                  >
                    <Link
                      to={link.to}
                      onClick={() => setMenuOpen(false)}
                      className="display block border-b border-[var(--border-subtle)] py-4 text-[2rem] leading-tight"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ),
              )}
              <div className="mt-8 flex flex-col gap-3">
                {user ? (
                  <>
                    <div className="mb-1 flex items-center gap-3 text-sm text-[var(--text-muted)]">
                      <span className="grid size-9 place-items-center rounded-full bg-lagoon-500/15 text-base">
                        {user.avatarEmoji ?? '🙂'}
                      </span>
                      {user.displayName ?? user.handle}
                    </div>
                    <ButtonLink to="/app" size="lg">
                      В приложение
                    </ButtonLink>
                    <Button variant="outline" size="lg" onClick={() => void logout()}>
                      Выйти
                    </Button>
                  </>
                ) : (
                  <>
                    <ButtonLink to="/register" size="lg">
                      Создать аккаунт
                    </ButtonLink>
                    <ButtonLink to="/login" variant="outline" size="lg">
                      Войти
                    </ButtonLink>
                  </>
                )}
              </div>
            </motion.nav>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}

function HeaderLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cx(
          'rounded-full px-4 py-2 text-sm transition-colors',
          isActive
            ? 'bg-[var(--surface-sunken)] font-medium text-[var(--text-strong)]'
            : 'text-[var(--text-muted)] hover:text-[var(--text-strong)]',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function NotificationBell() {
  const { unreadCount, items, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // A tap on a notification navigates; the panel must not follow you there.
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    // Not `relative` on a phone, deliberately: the panel below anchors to the
    // header instead of to the bell, because the bell is not at the edge of the
    // screen — the hamburger and the container padding sit to the right of it.
    <div className="sm:relative">
      <button
        type="button"
        aria-label="Уведомления"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          'relative grid size-10 place-items-center rounded-full border border-[var(--border-subtle)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-strong)]',
          open && 'bg-[var(--surface-sunken)]',
        )}
      >
        <span aria-hidden>🔔</span>
        {unreadCount > 0 ? (
          <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-clay-500 px-1 text-[0.65rem] font-semibold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      <AnimatePresence>
        {open ? (
          <>
            {/* The page dims behind the panel, so the panel reads as a layer
                above it rather than one more card — and a tap outside closes it.
                Portalled: the header's backdrop blur makes it the containing
                block of anything `fixed` inside it. Under the header (z-50). */}
            {createPortal(
              <motion.button
                key="scrim"
                type="button"
                aria-label="Закрыть уведомления"
                tabIndex={-1}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: DURATION.fast }}
                onClick={() => setOpen(false)}
                className="fixed inset-0 z-40 cursor-default bg-ink-950/50 sm:bg-ink-950/20"
              />,
              document.body,
            )}
            <motion.div
              key="panel"
              role="dialog"
              aria-label="Уведомления"
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: DURATION.fast, ease: EASE_OUT_EXPO }}
              /* On a phone: the width of the screen less a small margin, hung
                 under the header (`100%` is the header's own height, so this
                 follows it from 4rem to 4.5rem without repeating the number).
                 From sm up the bell has room, so the panel goes back to hanging
                 off it. */
              className="absolute inset-x-3 top-[calc(100%+0.5rem)] flex max-h-[min(30rem,calc(100dvh-6rem))] origin-top flex-col overflow-hidden rounded-lg border border-[var(--border-overlay)] bg-[var(--surface-overlay)] shadow-[var(--shadow-overlay)] sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[24rem] sm:origin-top-right"
            >
              <div className="flex items-center justify-between gap-3 px-5 pb-2 pt-4">
                <span className="text-base font-semibold tracking-tight">
                  Уведомления
                  {unreadCount > 0 ? (
                    <span className="ml-2 rounded-full bg-clay-500 px-2 py-0.5 align-middle text-xs font-semibold text-white">
                      {unreadCount}
                    </span>
                  ) : null}
                </span>
                {unreadCount > 0 ? (
                  <button
                    type="button"
                    className="rounded-lg px-2 py-1 text-xs font-medium text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-strong)]"
                    onClick={() => void markAllRead()}
                  >
                    Прочитать все
                  </button>
                ) : null}
              </div>
              {/* Items are cards of their own inside the panel's padding, so
                  the last one never runs into the rounded corner. */}
              <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-2 pb-3">
                {items.length === 0 ? (
                  <li className="px-3 py-8 text-center text-sm text-[var(--text-muted)]">
                    Пока пусто
                  </li>
                ) : (
                  items.slice(0, 12).map((n) => {
                    const body = (
                      <>
                        <span
                          aria-hidden
                          className={cx(
                            'mt-1.5 size-2 shrink-0 rounded-full',
                            n.readAt ? 'bg-transparent' : 'bg-clay-500',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline justify-between gap-3">
                            <span
                              className={cx(
                                'min-w-0 break-words text-sm leading-snug',
                                n.readAt ? 'font-medium' : 'font-semibold',
                              )}
                            >
                              {n.title}
                            </span>
                            <span className="shrink-0 text-[0.7rem] text-[var(--text-muted)]">
                              {timeAgo(n.createdAt)}
                            </span>
                          </span>
                          <span className="mt-1 line-clamp-3 break-words text-[0.8rem] leading-relaxed text-[var(--text-muted)]">
                            {n.body}
                          </span>
                        </span>
                      </>
                    );
                    const itemClass = cx(
                      'flex w-full gap-2.5 rounded-md px-3 py-3 text-left transition-colors hover:bg-[var(--surface-sunken)]',
                      !n.readAt && 'bg-clay-500/[0.07]',
                    );
                    return (
                      <li key={n.id}>
                        {n.href ? (
                          <Link
                            to={n.href}
                            onClick={() => {
                              setOpen(false);
                              if (!n.readAt) void markRead(n.id);
                            }}
                            className={itemClass}
                          >
                            {body}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            className={itemClass}
                            onClick={() => {
                              if (!n.readAt) void markRead(n.id);
                            }}
                          >
                            {body}
                          </button>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** «только что», «5 мин», «3 ч», «вчера», «12.10» — short enough for the row. */
function timeAgo(iso: string): string {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ч`;
  if (hours < 48) return 'вчера';
  const at = new Date(iso);
  return at.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}
